import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Gauge, Search, Download } from 'lucide-react';
import { DataTable, EmptyState, type ColumnDef } from '@vnetwork/ui/data';
import {
  Button,
  Input,
  Skeleton,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@vnetwork/ui/primitives';
import BusinessUsageShell from '@/pages/services/usage/BusinessUsageShell';
import { useComputeBusinessUsage, type FlavorUsageRow } from '@/api/opennebula';
import { formatNum } from '../_shared/analytics-format';
import { useDebounce } from '../shared';
import { DateTimeRangePicker } from '@/components/date-time-range-picker/DateTimeRangePicker';

// ---- Month options -------------------------------------------------------
// The 12-month preset dropdown, mirroring the CDN Business Usage page.
interface MonthOpt {
  key: string; // YYYY-MM
  label: string; // "Jun 2026"
}
function buildMonths(): MonthOpt[] {
  const now = new Date();
  const out: MonthOpt[] = [];
  for (let i = 0; i < 12; i++) {
    const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - i, 1));
    const key = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
    const label = d.toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' });
    out.push({ key, label });
  }
  return out;
}
function monthToRange(key: string): { from: string; to: string } {
  const [y, m] = key.split('-').map(Number);
  const start = new Date(Date.UTC(y, m - 1, 1));
  const nextMonth = new Date(Date.UTC(y, m, 1));
  const now = new Date();
  const to = nextMonth.getTime() > now.getTime() ? now : nextMonth;
  return { from: start.toISOString(), to: to.toISOString() };
}

// The period is either a preset month (monthly granularity) or an absolute
// start–end day window the user picks in the calendar (daily granularity). The
// compute usage store only keeps day + month tiers, so no sub-day option.
type RangeSel = { kind: 'month'; month: string } | { kind: 'custom'; from: string; to: string };

const shortDate = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });

function downloadCsv(rows: FlavorUsageRow[], periodSlug: string) {
  const header = 'Flavor,Resource type,Instance-months,Disk (GB-months),vCPU,RAM (GB)';
  const body = rows
    .map((r) =>
      [r.flavor, r.resource_type, r.instance_months, r.disk_gb_months, r.vcpu, r.ram_gb].join(','),
    )
    .join('\n');
  const blob = new Blob([`${header}\n${body}`], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `compute-business-usage-${periodSlug}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * Compute (OpenNebula) Business Usage — per-service, per-flavor monthly billing
 * basis. Reuses the shared `BusinessUsageShell`; the data + KPIs + breakdown are
 * compute-specific (instance-months and disk GB-months per flavor, derived from
 * the central cloud usage store), so they live here — matching the WAAP tab model.
 */
export function BusinessUsage() {
  const { serviceId } = useParams<{ serviceId: string }>();

  const months = useMemo(buildMonths, []);
  const [sel, setSel] = useState<RangeSel>({ kind: 'month', month: months[0].key });

  const range = useMemo(
    () => (sel.kind === 'month' ? monthToRange(sel.month) : { from: sel.from, to: sel.to }),
    [sel],
  );
  // Month presets bill monthly (granularity=month); a custom day window reads the
  // day tier — the only two tiers the compute usage store keeps.
  const granularity = sel.kind === 'month' ? 'month' : 'day';
  const periodLabel =
    sel.kind === 'month'
      ? months.find((m) => m.key === sel.month)?.label ?? sel.month
      : `${shortDate(range.from)} – ${shortDate(range.to)}`;
  const periodSlug =
    sel.kind === 'month' ? sel.month : `${range.from.slice(0, 10)}_${range.to.slice(0, 10)}`;

  const [searchInput, setSearchInput] = useState('');
  const search = useDebounce(searchInput, 300);

  const usageQ = useComputeBusinessUsage(serviceId, {
    from: range.from,
    to: range.to,
    granularity,
  });
  const loading = usageQ.isLoading;

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const base = q
      ? (usageQ.data ?? []).filter((r) => (r.flavor ?? '').toLowerCase().includes(q))
      : usageQ.data ?? [];
    return [...base].sort((a, b) => b.instance_months - a.instance_months);
  }, [usageQ.data, search]);

  // KPI strip: total instance-months, how many distinct flavors ran, and total
  // disk GB-months — the three headline figures the monthly statement rolls up.
  const kpis = useMemo(() => {
    const all = usageQ.data ?? [];
    const totalInstanceMonths = all.reduce((a, r) => a + (r.instance_months ?? 0), 0);
    const totalDiskGbMonths = all.reduce((a, r) => a + (r.disk_gb_months ?? 0), 0);
    return [
      { label: 'Instance-months', value: formatNum(totalInstanceMonths) },
      { label: 'Flavors', value: formatNum(all.length) },
      { label: 'Disk (GB-months)', value: formatNum(totalDiskGbMonths) },
    ];
  }, [usageQ.data]);

  const columns = useMemo<ColumnDef<FlavorUsageRow, unknown>[]>(
    () => [
      {
        id: 'flavor',
        accessorKey: 'flavor',
        header: 'Flavor',
        cell: ({ row }) => <span style={{ fontWeight: 500 }}>{row.original.flavor}</span>,
      },
      {
        id: 'resource_type',
        accessorKey: 'resource_type',
        header: 'Resource',
        cell: ({ row }) => (
          <span style={{ color: 'var(--vn-fg-muted)' }}>{row.original.resource_type}</span>
        ),
      },
      {
        id: 'instance_months',
        accessorKey: 'instance_months',
        header: 'Instance-months',
        cell: ({ row }) => formatNum(row.original.instance_months),
      },
      {
        id: 'disk_gb_months',
        accessorKey: 'disk_gb_months',
        header: 'Disk (GB-months)',
        cell: ({ row }) => (
          <span style={{ color: 'var(--vn-fg-muted)' }}>{formatNum(row.original.disk_gb_months)}</span>
        ),
      },
      {
        id: 'vcpu',
        accessorKey: 'vcpu',
        header: 'vCPU',
        cell: ({ row }) => (
          <span style={{ color: 'var(--vn-fg-muted)' }}>{formatNum(row.original.vcpu)}</span>
        ),
      },
      {
        id: 'ram_gb',
        accessorKey: 'ram_gb',
        header: 'RAM (GB)',
        cell: ({ row }) => (
          <span style={{ color: 'var(--vn-fg-muted)' }}>{formatNum(row.original.ram_gb)}</span>
        ),
      },
    ],
    [],
  );

  return (
    <BusinessUsageShell
      icon={<Gauge size={22} />}
      description="Per-flavor instance-months and disk GB-months for this compute service."
      basis="Usage is instance-months per flavor: the sum of VM run-hours ÷ 24 ÷ days in the billed period, plus disk GB-months — the basis your monthly compute statement reconciles against."
      period={
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <Select
            value={sel.kind === 'month' ? sel.month : ''}
            onValueChange={(v) => setSel({ kind: 'month', month: v })}
          >
            <SelectTrigger size="md" style={{ width: 170, flexShrink: 0 }}>
              <SelectValue placeholder="Custom range" />
            </SelectTrigger>
            <SelectContent>
              {months.map((m) => (
                <SelectItem key={m.key} value={m.key}>
                  {m.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <DateTimeRangePicker
            value={sel.kind === 'custom' ? { from: sel.from, to: sel.to } : { from: '', to: '' }}
            onChange={(v) => {
              if (v.from && v.to) setSel({ kind: 'custom', from: v.from, to: v.to });
            }}
            placeholder="Custom range"
          />
        </div>
      }
      actions={
        <Button
          variant="secondary"
          size="md"
          startIcon={<Download size={14} />}
          onClick={() => downloadCsv(rows, periodSlug)}
          disabled={!rows.length}
        >
          Export CSV
        </Button>
      }
      kpis={kpis}
      kpisLoading={loading}
      periodLabel={periodLabel}
      search={
        <Input
          inputSize="md"
          placeholder="Filter flavors"
          startSlot={<Search />}
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          style={{ width: 260, flexShrink: 0 }}
        />
      }
    >
      {loading ? (
        <Skeleton style={{ height: 280 }} />
      ) : (
        <DataTable
          columns={columns}
          data={rows}
          getRowId={(r) => `${r.resource_type}:${r.flavor}`}
          emptyState={
            <EmptyState
              icon={<Gauge size={28} />}
              title="No usage"
              description="No usage recorded for this period."
            />
          }
        />
      )}
    </BusinessUsageShell>
  );
}
